import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import ChatModal from '../components/ChatModal';
import { apiClient } from '../utils/apiClient';
import { extractPrimaryImage } from '../utils/imageHelpers';
import { useToast } from '../hooks/useToast';

const OfferDetails = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatModal, setChatModal] = useState({ isOpen: false, offer: null });
  const [payingFee, setPayingFee] = useState(false);
  const [responding, setResponding] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptInfinite, setAcceptInfinite] = useState(false);
  const [acceptDurationHours, setAcceptDurationHours] = useState('48');
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    if (offerId) {
      fetchOfferDetails();
    }
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get(`/api/second-hand-offers/${offerId}`);

      if (data.success) {
        setOffer(data.data);
      } else {
        setError(data.message || t('offerDetails.failedToLoad'));
      }
    } catch (error) {
      console.error('Error fetching offer:', error);
      setError(error.message || t('offerDetails.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handlePayFee = async () => {
    // TODO: Replace window.confirm with a proper confirmation dialog/toast
    if (!window.confirm(t('offerDetails.payFeeConfirm', { amount: offer.feeAmount || 1 }))) {
      return;
    }

    try {
      setPayingFee(true);
      const data = await apiClient.post(`/api/second-hand-offers/${offerId}/pay-fee`, {});

      if (data.success) {
        toast.success(t('offerDetails.feePaidSuccess'));
        // Update only buyer fee fields since seller fee is handled on acceptance
        setOffer(prev => ({ 
          ...prev, 
          buyerFeePaid: true,
          feePaid: data.data.bothPaid || false
        }));
      } else {
        toast.error(data.message || t('offerDetails.failedToPayFee'));
      }
    } catch (error) {
      console.error('Error paying fee:', error);
      toast.error(error.message || t('offerDetails.failedToPayFee'));
    } finally {
      setPayingFee(false);
    }
  };

  const openAcceptModal = () => {
    if (!offer?.productId) {
      setAcceptInfinite(false);
      setAcceptDurationHours('48');
    } else {
      const product = offer.productId;
      if (product.defaultOfferInfinite) {
        setAcceptInfinite(true);
        setAcceptDurationHours('');
      } else {
        setAcceptInfinite(false);
        if (typeof product.defaultOfferDurationHours !== 'undefined') {
          setAcceptDurationHours(String(product.defaultOfferDurationHours));
        } else {
          setAcceptDurationHours('48');
        }
      }
    }
    setAcceptModalOpen(true);
  };

  const handleRespondToOffer = async (action, overrides = {}) => {
    if (!offer) return false;

    let success = false;
    try {
      setResponding(true);
      const payload = {
        action,
        sellerResponse: responseMessage.trim() ? responseMessage.trim() : undefined
      };

      if (action === 'accept') {
        const isInfinite = overrides.isInfinite ?? acceptInfinite;
        const durationInput = overrides.durationHours ?? acceptDurationHours;
        payload.isInfinite = isInfinite;
        if (!isInfinite) {
          const parsed = parseInt(durationInput, 10);
          const bounded = !isNaN(parsed) && parsed > 0 ? Math.min(parsed, 720) : 48;
          payload.durationHours = bounded;
        }
      }

      const data = await apiClient.put(`/api/second-hand-offers/${offerId}/respond`, payload);

      if (data.success) {
        success = true;
        toast.success(t('offerDetails.offerActionSuccess', { action }));
        setResponseMessage('');
        await fetchOfferDetails();
      } else {
        toast.error(data.message || t('offerDetails.offerActionFailed', { action }));
      }
    } catch (err) {
      console.error(`Error ${action}ing offer:`, err);
      toast.error(err.message || t('offerDetails.offerActionFailed', { action }));
    } finally {
      setResponding(false);
    }

    return success;
  };

  const confirmAccept = async () => {
    const success = await handleRespondToOffer('accept', {
      isInfinite: acceptInfinite,
      durationHours: acceptDurationHours
    });
    if (success) {
      setAcceptModalOpen(false);
    }
  };

  const handleReject = async () => {
    await handleRespondToOffer('reject');
  };

  const handleWithdrawOffer = async () => {
    if (!offer) return;
    // TODO: Replace window.confirm with a proper confirmation dialog/toast
    if (!window.confirm(t('offerDetails.withdrawConfirm'))) {
      return;
    }

    try {
      setWithdrawing(true);
      const data = await apiClient.put(`/api/second-hand-offers/${offerId}/withdraw`, {});

      if (data.success) {
        toast.success(t('offerDetails.withdrawSuccess'));
        await fetchOfferDetails();
      } else {
        toast.error(data.message || t('offerDetails.failedToWithdraw'));
      }
    } catch (err) {
      console.error('Error withdrawing offer:', err);
      toast.error(err.message || t('offerDetails.failedToWithdraw'));
    } finally {
      setWithdrawing(false);
    }
  };

  const resolveEntityId = (entity) => {
    if (!entity) return null;
    if (typeof entity === 'string') return entity;
    if (typeof entity === 'object') {
      if (entity._id) return entity._id;
      if (entity.id) return entity.id;
      if (typeof entity.toString === 'function' && entity !== entity.toString()) {
        return entity.toString();
      }
    }
    return null;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-400 bg-yellow-400/10',
      accepted: 'text-green-400 bg-green-400/10',
      rejected: 'text-red-400 bg-red-400/10',
      expired: 'text-gray-400 bg-gray-400/10',
      withdrawn: 'text-gray-400 bg-gray-400/10'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      accepted: '✅',
      rejected: '❌',
      expired: '⏰',
      withdrawn: '🚫'
    };
    return icons[status] || '❓';
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('offerDetails.pleaseLogin')}</h2>
          <p className="text-gray-500 dark:text-gray-400">{t('offerDetails.loginRequired')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <SciFiBackground>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
        </div>
      </SciFiBackground>
    );
  }

  if (error) {
    return (
      <SciFiBackground>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('error')}</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/offers/my-offers')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              {t('offerDetails.backToMyOffers')}
            </button>
          </div>
        </div>
      </SciFiBackground>
    );
  }

  if (!offer) {
    return (
      <SciFiBackground>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('offerDetails.offerNotFound')}</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4">{t('offerDetails.offerNotFoundDesc')}</p>
            <button
              onClick={() => navigate('/offers/my-offers')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              {t('offerDetails.backToMyOffers')}
            </button>
          </div>
        </div>
      </SciFiBackground>
    );
  }

  const userId = resolveEntityId(user);
  const isBuyer = resolveEntityId(offer.buyerId) === userId;
  const isSeller = resolveEntityId(offer.sellerId) === userId;
  const bothPaid = offer.buyerFeePaid && offer.sellerFeePaid;
  const chatAvailable = offer.chatUnlocked || offer.status === 'accepted' || bothPaid;
  const awaitingBuyerFee = offer.status === 'pending' && offer.provisionalAccepted && !offer.buyerFeePaid;
  const awaitingSellerAction = isSeller && offer.status === 'pending' && !offer.provisionalAccepted;
  const canWithdraw = isBuyer && offer.status === 'pending';
  const acceptDurationTooHigh = !acceptInfinite && acceptDurationHours && parseInt(acceptDurationHours, 10) > 720;

  return (
    <SciFiBackground>
      <Navbar />
      <div className="relative z-10 min-h-screen">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(isBuyer ? '/offers/my-offers' : '/offers/received')}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 inline-flex items-center"
            >
              ← {isBuyer ? t('offerDetails.backToMyOffers') : t('offerDetails.backToReceivedOffers')}
            </button>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('offerDetails.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {isBuyer ? t('offerDetails.yourOfferOn') : t('offerDetails.offerReceivedFor')}: {offer.productId?.name}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Product Info */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    {(() => {
                      const { url, thumbnailUrl } = extractPrimaryImage(offer.productId);
                      const imageSrc = url || thumbnailUrl;
                      return imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={offer.productId?.name || 'Product image'}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <span className="text-3xl">📦</span>
                      );
                    })()}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{offer.productId?.name}</h2>
                    <div className="space-y-1 text-sm text-gray-500 dark:text-gray-400">
                      <p>{t('offerDetails.reservePrice')}: <span className="text-yellow-400">{formatPrice(offer.productId?.reservePrice)}</span></p>
                      <p>{t('offerDetails.condition')}: <span className="text-orange-400">{offer.productId?.condition}</span></p>
                      <p>{t('offerDetails.category')}: <span className="text-cyan-400">{offer.productId?.category}</span></p>
                    </div>
                  </div>
                </div>

                {/* Offer Details */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">{t('offerDetails.yourOffer')}</p>
                      <p className="text-yellow-400 font-bold text-lg">{formatPrice(offer.offerAmount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">{t('offerDetails.status')}</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(offer.status)}`}>
                        {getStatusIcon(offer.status)} {offer.status.toUpperCase()}
                      </span>
                    </div>
                    {offer.status === 'pending' && (
                      <div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">{t('offerDetails.timeLeft')}</p>
                        <p className="text-gray-900 dark:text-white text-sm">
                          {offer.isInfinite ? '∞' : (!offer.expiresAt ? t('offerDetails.pendingActivation') : (() => { const end=new Date(offer.expiresAt).getTime(); const diff=end-Date.now(); if(diff<=0) return t('offerDetails.expired'); const h=Math.floor(diff/3600000); const m=Math.floor((diff%3600000)/60000); return h>72? `${Math.ceil(diff/86400000)}d` : `${h}h ${m}m`; })())}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">{isBuyer ? t('offerDetails.seller') : t('offerDetails.buyer')}</p>
                      <p className="text-gray-900 dark:text-white">{isBuyer ? offer.sellerId?.username : offer.buyerId?.username}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">{t('offerDetails.date')}</p>
                      <p className="text-gray-900 dark:text-white text-sm">{formatDate(offer.createdAt)}</p>
                    </div>
                  </div>

                  {offer.message && (
                    <div className="mb-4">
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">{t('offerDetails.message')}</p>
                      <p className="text-gray-600 dark:text-gray-300 italic">"{offer.message}"</p>
                    </div>
                  )}

                  {offer.sellerResponse && (
                    <div className="mb-4">
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">{t('offerDetails.sellerResponseLabel')}</p>
                      <p className="text-gray-600 dark:text-gray-300 italic">"{offer.sellerResponse}"</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(offer.respondedAt)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('offerDetails.actions')}</h3>

                {chatAvailable && (
                  <div className="bg-green-600/20 border border-green-600/50 rounded-lg p-3 text-center">
                    <div className="text-green-400 font-semibold">✅ {t('offerDetails.chatUnlocked')}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
                      {isSeller
                        ? t('offerDetails.sellerChatMsg')
                        : t('offerDetails.buyerChatMsg')}
                    </div>
                    <button
                      onClick={() => setChatModal({ isOpen: true, offer })}
                      className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-300 text-sm"
                    >
                      💬 {t('offerDetails.startChat')}
                    </button>
                  </div>
                )}

                {!chatAvailable && awaitingBuyerFee && (
                  <div className="bg-yellow-600/20 border border-yellow-600/50 rounded-lg p-3 space-y-3">
                    <div className="text-yellow-400 font-semibold text-sm">
                      {isSeller
                        ? t('offerDetails.sellerAwaitingFee')
                        : t('offerDetails.buyerAwaitingFee')}
                    </div>
                    {isBuyer && !offer.buyerFeePaid && (
                      <button
                        onClick={handlePayFee}
                        disabled={payingFee}
                        className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-300 text-sm disabled:opacity-50"
                      >
                        {payingFee ? t('offerDetails.processing') : t('offerDetails.payFeeButton', { amount: offer.feeAmount || 1 })}
                      </button>
                    )}
                  </div>
                )}

                {awaitingSellerAction && (
                  <div className="space-y-3">
                    <textarea
                      value={responseMessage}
                      onChange={(e) => setResponseMessage(e.target.value)}
                      placeholder={t('offerDetails.optionalResponsePlaceholder')}
                      rows="3"
                      maxLength="500"
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm"
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={openAcceptModal}
                        disabled={responding}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-300 disabled:opacity-50 text-sm"
                      >
                        {responding ? t('offerDetails.processing') : t('offerDetails.accept')}
                      </button>
                      <button
                        onClick={handleReject}
                        disabled={responding}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-300 disabled:opacity-50 text-sm"
                      >
                        {responding ? t('offerDetails.processing') : t('offerDetails.reject')}
                      </button>
                    </div>
                  </div>
                )}

                {isSeller && awaitingBuyerFee && (
                  <div className="bg-blue-600/20 border border-blue-600/50 rounded-lg p-3 mt-4 text-sm text-blue-700 dark:text-blue-200">
                    {t('offerDetails.buyerNotifiedFee')}
                  </div>
                )}

                {canWithdraw && (
                  <button
                    onClick={handleWithdrawOffer}
                    disabled={withdrawing}
                    className="w-full mt-4 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-300 text-sm disabled:opacity-50"
                  >
                    {withdrawing ? t('offerDetails.processing') : t('offerDetails.withdrawOffer')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

        {acceptModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
              <div className="text-center mb-6">
                <div className="text-4xl mb-4">💰</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('offerDetails.acceptOfferTitle')}</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {t('offerDetails.acceptOfferDesc')}
                </p>
              </div>

              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-6 text-sm text-gray-700 dark:text-gray-200 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('offerDetails.productLabel')}:</span>
                  <span className="text-gray-900 dark:text-white font-medium">{offer.productId?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('offerDetails.offerAmountLabel')}:</span>
                  <span className="text-yellow-400 font-bold">{formatPrice(offer.offerAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('offerDetails.chatFeeLabel')}:</span>
                  <span className="text-green-400 font-bold">{offer.feeAmount || 1} TND</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('offerDetails.buyer')}:</span>
                  <span className="text-gray-900 dark:text-white">{offer.buyerId?.username}</span>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <button
                  type="button"
                  onClick={() => setAcceptInfinite(true)}
                  className={`w-full text-left flex items-start space-x-3 p-3 rounded-lg border transition-colors duration-200 ${acceptInfinite ? 'bg-gray-100 dark:bg-gray-700/70 border-green-500/60 ring-2 ring-green-500/40' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  <span className={`mt-1 inline-flex h-4 w-4 rounded-full border-2 ${acceptInfinite ? 'border-green-400 bg-green-500' : 'border-gray-500'}`}></span>
                  <div>
                    <p className="text-gray-900 dark:text-white font-medium">{t('offerDetails.foreverOption')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('offerDetails.foreverDesc')}</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setAcceptInfinite(false)}
                  className={`w-full text-left flex items-start space-x-3 p-3 rounded-lg border transition-colors duration-200 ${!acceptInfinite ? 'bg-gray-100 dark:bg-gray-700/70 border-blue-500/60 ring-2 ring-blue-500/40' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  <span className={`mt-1 inline-flex h-4 w-4 rounded-full border-2 ${!acceptInfinite ? 'border-blue-400 bg-blue-500' : 'border-gray-500'}`}></span>
                  <div className="flex-1">
                    <p className="text-gray-900 dark:text-white font-medium">{t('offerDetails.countdownOption')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('offerDetails.countdownDesc')}</p>
                    <div className="mt-2 flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        max="720"
                        disabled={acceptInfinite}
                        value={acceptDurationHours}
                        onChange={(e) => setAcceptDurationHours(e.target.value)}
                        className="w-28 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-green-400 text-sm"
                        placeholder="48"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t('offerDetails.hours')}</span>
                    </div>
                  </div>
                </button>
                {!acceptInfinite && acceptDurationHours === '' && (
                  <p className="text-xs text-yellow-400">{t('offerDetails.provideDuration')}</p>
                )}
                {acceptDurationTooHigh && (
                  <p className="text-xs text-red-400">{t('offerDetails.maxDuration')}</p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setAcceptModalOpen(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-700 dark:text-white rounded-lg transition-colors duration-300"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={confirmAccept}
                  disabled={responding || (!acceptInfinite && (!acceptDurationHours || acceptDurationTooHigh))}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-300 disabled:opacity-50"
                >
                  {responding ? t('offerDetails.processing') : t('offerDetails.confirmAcceptance')}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Chat Modal */}
      <ChatModal
        isOpen={chatModal.isOpen}
        onClose={() => setChatModal({ isOpen: false, offer: null })}
        offer={chatModal.offer}
        currentUser={user}
      />
    </SciFiBackground>
  );
};

export default OfferDetails;
